/**
 * nutriHealLibrary.ts
 * Curated static library of therapeutic recipes and base guidelines for NatureNani.
 * Replaces dynamic meal generation with deterministic, high-quality, culturally-neutral food intelligence.
 */

export const RecipeCategory = {
  RESPIRATORY: 'RESPIRATORY',
  DIGESTIVE: 'DIGESTIVE',
  CARDIOVASCULAR: 'CARDIOVASCULAR',
  RENAL: 'RENAL',
  DERMATOLOGICAL: 'DERMATOLOGICAL',
  NEUROLOGICAL: 'NEUROLOGICAL',
  MUSCULOSKELETAL: 'MUSCULOSKELETAL',
  ENDOCRINE: 'ENDOCRINE',
  IMMUNE: 'IMMUNE',
  HERBAL_TEAS: 'HERBAL_TEAS'
} as const;

export type RecipeCategory = typeof RecipeCategory[keyof typeof RecipeCategory];

export type DoshaType = 'Vata' | 'Pitta' | 'Kapha' | 'All';
export type ConditionType = 'acute' | 'chronic' | 'both';

export interface NutriHealRecipe {
  id: string;
  name: string;
  category: RecipeCategory;
  ailmentTags: string[];
  doshaSuitability: DoshaType[];
  condition: ConditionType;
  prepTime: string;
  servings: number;
  ingredients: string[];
  steps: string[];
  ayurvedicNote: string;
  therapeuticBenefit: string;
  imageUrl: string;
  imageSearchTerm: string; // Used by the fetch script
  imageCredit: string;
  herbalTea: boolean;
}

export interface AilmentGuidelines {
  embrace: string[];
  avoid: string[];
  switches: string[];
  timing: string;
}

export const NUTRIHEAL_RECIPES: NutriHealRecipe[] = [
  // --- RESPIRATORY ---
  {
    id: 'khichdi-classic',
    name: 'Classic Mung Dal Khichdi',
    category: RecipeCategory.RESPIRATORY,
    ailmentTags: ['cold', 'cough', 'rhinitis', 'sinusitis', 'fever'],
    doshaSuitability: ['All'],
    condition: 'both',
    prepTime: '25 min',
    servings: 2,
    ingredients: [
      '½ cup split yellow mung dal',
      '¼ cup basmati rice',
      '1 tsp ghee',
      '½ tsp cumin seeds',
      '½ tsp turmeric powder',
      '½ tsp freshly grated ginger',
      '1 pinch hing (asafoetida)',
      '2.5 cups water',
      'Rock salt to taste',
      'Fresh coriander for garnish'
    ],
    steps: [
      'Rinse dal and rice together until water runs clear. Soak for 15 minutes.',
      'Heat ghee in a pot. Add cumin seeds and hing; let them splutter.',
      'Add grated ginger and sauté for 30 seconds until fragrant.',
      'Add drained dal and rice, coating them with the spices. Toast for 1 minute.',
      'Add water, turmeric, and rock salt. Bring to a boil.',
      'Reduce heat, cover, and simmer for 20-25 minutes until soft and porridge-like.',
      'Garnish with coriander and serve warm.'
    ],
    ayurvedicNote: 'Charaka Samhita classifies mung dal as the most Sattvic legume — light (laghu), easy to digest, and balancing for all three doshas.',
    therapeuticBenefit: 'Restores digestive fire (Agni) while providing essential nourishment without taxing the system during illness.',
    imageUrl: '/images/nutriheal/khichdi-classic.jpg',
    imageSearchTerm: 'yellow lentil rice khichdi bowl plated warm',
    imageCredit: 'Unsplash',
    herbalTea: false
  },
  {
    id: 'tulsi-pepper-tea',
    name: 'Tulsi Pepper Immunity Tea',
    category: RecipeCategory.HERBAL_TEAS,
    ailmentTags: ['cold', 'cough', 'sinusitis', 'respiratory congestion'],
    doshaSuitability: ['Kapha', 'Vata'],
    condition: 'acute',
    prepTime: '10 min',
    servings: 1,
    ingredients: [
      '10-12 fresh tulsi leaves (Holy Basil)',
      '½ tsp black peppercorns, crushed',
      '1 inch fresh ginger, grated',
      '1 tsp honey',
      '1.5 cups water'
    ],
    steps: [
      'Bring water to a boil in a small pot.',
      'Add tulsi leaves, crushed peppercorns, and grated ginger.',
      'Simmer for 5-7 minutes until the water is aromatic.',
      'Strain into a mug.',
      'Add honey only when the tea is warm, not boiling.',
      'Sip slowly throughout the day.'
    ],
    ayurvedicNote: 'Charaka Samhita classifies Tulsi as an "Elixir of Life" for its ability to clear respiratory passages and reduce fever.',
    therapeuticBenefit: 'Potent expectorant and antiviral; clears chest congestion and eases breathing.',
    imageUrl: '/images/nutriheal/tulsi-pepper-tea.jpg',
    imageSearchTerm: 'herbal tea cup green herbs warm drink',
    imageCredit: 'Unsplash',
    herbalTea: true
  },
  {
    id: 'steamed-veg-bowl',
    name: 'Lightly Steamed Vegetable Bowl',
    category: RecipeCategory.DIGESTIVE,
    ailmentTags: ['indigestion', 'gut inflammation', 'low appetite', 'respiratory'],
    doshaSuitability: ['Pitta', 'Vata'],
    condition: 'both',
    prepTime: '15 min',
    servings: 1,
    ingredients: [
      '1 cup mixed seasonal vegetables (zucchini, carrots, broccoli)',
      '½ tsp sesame oil',
      '1 tsp fresh lime juice',
      '½ tsp grated ginger',
      'Pinch of rock salt'
    ],
    steps: [
      'Chop vegetables into uniform bite-sized pieces.',
      'Steam for 5-8 minutes until tender-crisp (do not overcook).',
      'Whisk lime juice, sesame oil, and ginger together in a small bowl.',
      'Toss the warm vegetables in the dressing.',
      'Season with a pinch of rock salt and serve immediately.'
    ],
    ayurvedicNote: 'Cooking vegetables lightly (Sanskar) makes them "Laghu" (light) while preserving their life force (Prana).',
    therapeuticBenefit: 'Provides essential fiber and vitamins without causing gas or bloating common with raw salads.',
    imageUrl: '/images/nutriheal/steamed-veg-bowl.jpg',
    imageSearchTerm: 'steamed broccoli carrots vegetables bowl healthy',
    imageCredit: 'Unsplash',
    herbalTea: false
  },
  {
    id: 'ginger-honey-tea',
    name: 'Ginger-Honey-Lemon Healing Tea',
    category: RecipeCategory.HERBAL_TEAS,
    ailmentTags: ['cold', 'cough', 'sore throat', 'congestion'],
    doshaSuitability: ['Vata', 'Kapha'],
    condition: 'acute',
    prepTime: '10 min',
    servings: 1,
    ingredients: [
      '2 cm fresh ginger root, sliced',
      '½ lemon, juiced',
      '1 tsp raw honey',
      '1.5 cups water',
      'Optional: 1 cinnamon stick or 2 tulsi leaves'
    ],
    steps: [
      'Bring water to a boil in a small saucepan.',
      'Add sliced ginger (and optional cinnamon/tulsi).',
      'Simmer on low heat for 8-10 minutes.',
      'Remove from heat and let it cool for 2-3 minutes until warm but not scalding.',
      'Strain into a mug and add lemon juice.',
      'Stir in honey only after cooling to preserve its medicinal properties.',
      'Sip slowly every 3-4 hours.'
    ],
    ayurvedicNote: 'Ashtanga Hridayam describes ginger as the supreme Mahaushadha (great medicine) for clearing Kapha and kindling Agni.',
    therapeuticBenefit: 'Antiviral and anti-inflammatory; soothes the throat and thins mucous for easier clearing.',
    imageUrl: '/images/nutriheal/ginger-honey-tea.jpg',
    imageSearchTerm: 'ginger lemon tea mug steam warm cup',
    imageCredit: 'Unsplash',
    herbalTea: true
  },
  {
    id: 'turmeric-golden-milk',
    name: 'Turmeric Golden Milk',
    category: RecipeCategory.RESPIRATORY,
    ailmentTags: ['cough', 'inflammation', 'sore throat', 'joint pain'],
    doshaSuitability: ['Vata', 'Kapha'],
    condition: 'both',
    prepTime: '10 min',
    servings: 1,
    ingredients: [
      '1 cup milk (dairy, oat, or coconut)',
      '½ tsp turmeric powder',
      '¼ tsp ground black pepper',
      '¼ tsp ground ginger',
      '1 small cinnamon stick',
      '1 tsp honey or maple syrup'
    ],
    steps: [
      'In a small saucepan, combine milk, turmeric, pepper, ginger, and cinnamon.',
      'Whisk to combine and warm over medium heat until small bubbles form (don\'t boil).',
      'Reduce heat and simmer for 5 minutes.',
      'Strain into a mug and let it cool slightly.',
      'Add sweetener and stir well. Drink warm before bed.'
    ],
    ayurvedicNote: 'Turmeric combined with black pepper significantly increases the bioavailability of curcumin, the active anti-inflammatory compound.',
    therapeuticBenefit: 'Potent anti-inflammatory that soothes inflamed respiratory passages and supports the immune system.',
    imageUrl: '/images/nutriheal/turmeric-golden-milk.jpg',
    imageSearchTerm: 'turmeric golden milk latte mug cozy',
    imageCredit: 'Unsplash',
    herbalTea: false
  },
  {
    id: 'garlic-broth',
    name: 'Garlic Healing Broth',
    category: RecipeCategory.RESPIRATORY,
    ailmentTags: ['cold', 'flu', 'low immunity'],
    doshaSuitability: ['Vata', 'Kapha'],
    condition: 'acute',
    prepTime: '40 min',
    servings: 4,
    ingredients: [
      '1 whole head of garlic, peeled and smashed',
      '1 large onion, chopped',
      '2 carrots, sliced',
      '2 stalks celery, sliced',
      '1 inch fresh ginger, sliced',
      '1 tsp turmeric powder',
      '1 liter vegetable broth or water',
      'Fresh thyme and parsley',
      'Salt and pepper to taste'
    ],
    steps: [
      'In a large pot, sauté onions and garlic until translucent.',
      'Add carrots, celery, and ginger; sauté for another 3 minutes.',
      'Stir in turmeric, salt, and pepper.',
      'Add broth and herbs. Bring to a boil.',
      'Reduce heat and simmer uncovered for 30 minutes.',
      'Strain for a clear broth or leave vegetables in for a hearty soup.'
    ],
    ayurvedicNote: 'Garlic (Lashuna) is highly valued in Ayurveda for its "Ushna" (hot) potency, which effectively clears cold-related Kapha stagnation.',
    therapeuticBenefit: 'Strong antimicrobial and antiviral properties; warms the body and stimulates circulation.',
    imageUrl: '/images/nutriheal/garlic-broth.jpg',
    imageSearchTerm: 'garlic vegetable broth soup bowl steam',
    imageCredit: 'Unsplash',
    herbalTea: false
  },
  // --- DIGESTIVE ---
  {
    id: 'millet-porridge',
    name: 'Soaked Millet Porridge',
    category: RecipeCategory.DIGESTIVE,
    ailmentTags: ['indigestion', 'bloating', 'gut health', 'constipation', 'vata', 'pitta'],
    doshaSuitability: ['All'],
    condition: 'chronic',
    prepTime: '20 min (+6h soaking)',
    servings: 2,
    ingredients: [
      '½ cup foxtail or barnyard millet',
      '2 cups water or nut milk',
      '1 tsp ghee',
      '½ tsp cumin seeds',
      'Pinch of rock salt',
      'Optional: Stewed apples or berries for sweeteners'
    ],
    steps: [
      'Soak millet for at least 6 hours before cooking to neutralize phytic acid.',
      'Drain and rinse the millet thoroughly.',
      'In a pot, bring water to a boil and add the millet.',
      'Reduce heat to low, cover, and simmer for 15-20 minutes until water is absorbed and millet is soft.',
      'In a small pan, heat ghee and cumin seeds until they pop, then pour over the porridge.',
      'Add salt and serve warm.'
    ],
    ayurvedicNote: 'Millets are "Laghu" (light) and "Rooksha" (dry), making them excellent for balancing Kapha and clearing digestive dampness.',
    therapeuticBenefit: 'High fiber and easy digestibility help regulate bowel movements and heal the gut lining.',
    imageUrl: '/images/nutriheal/millet-porridge.jpg',
    imageSearchTerm: 'millet porridge warm bowl cooked soft grain',
    imageCredit: 'Unsplash',
    herbalTea: false
  },
  {
    id: 'coconut-vegetable-stew',
    name: 'Coconut Vegetable Stew',
    category: RecipeCategory.DIGESTIVE,
    ailmentTags: ['acid reflux', 'inflammation', 'gut reset', 'pitta'],
    doshaSuitability: ['Pitta', 'Vata'],
    condition: 'both',
    prepTime: '30 min',
    servings: 3,
    ingredients: [
      '1 cup mixed vegetables (carrots, beans, pumpkin)',
      '1 cup coconut milk',
      '1 tsp coconut oil',
      '½ tsp mustard seeds',
      '10 curry leaves',
      '½ tsp turmeric powder',
      '1 inch ginger, julienned',
      'Salt to taste'
    ],
    steps: [
      'Steam the mixed vegetables until just tender.',
      'Heat coconut oil in a pan; add mustard seeds and curry leaves until they crackle.',
      'Add julienned ginger and sauté briefly.',
      'Add the steamed vegetables, turmeric, and salt. Stir to coat.',
      'Pour in the coconut milk and simmer on low heat for 5-7 minutes. Do not boil.',
      'Serve with steamed basmati rice.'
    ],
    ayurvedicNote: 'Coconut is cooling (Sheeta) and heavy (Guru), which effectively pacifies the heat of Pitta and the dryness of Vata.',
    therapeuticBenefit: 'Provides a soothing, anti-inflammatory effect on the digestive tract; ideal for those with high acidity.',
    imageUrl: '/images/nutriheal/coconut-vegetable-stew.jpg',
    imageSearchTerm: 'coconut vegetable stew bowl warm curry',
    imageCredit: 'Unsplash',
    herbalTea: false
  },
  {
    id: 'fennel-ajwain-tea',
    name: 'Fennel & Ajwain Digestive Tea',
    category: RecipeCategory.HERBAL_TEAS,
    ailmentTags: ['bloating', 'gas', 'indigestion', 'cramps', 'pitta', 'vata', 'kapha'],
    doshaSuitability: ['All'],
    condition: 'acute',
    prepTime: '8 min',
    servings: 1,
    ingredients: [
      '1 tsp fennel seeds',
      '½ tsp ajwain (carom) seeds',
      '1 slice fresh ginger',
      'Pinch of black salt (optional)',
      '1.5 cups water'
    ],
    steps: [
      'Boil water in a small pot.',
      'Add fennel, ajwain, and ginger.',
      'Simmer on low for 5 minutes until the water turns golden.',
      'Strain into a cup.',
      'Add a pinch of black salt if desired for extra carminative effect.',
      'Drink warm after meals.'
    ],
    ayurvedicNote: 'Ajwain is a powerful "Deepana" (appetizer) and "Pachana" (digestant) that specifically targets gas and intestinal cramps.',
    therapeuticBenefit: 'Relieves intestinal gas, bloating, and stomach discomfort almost immediately after consumption.',
    imageUrl: '/images/nutriheal/fennel-ajwain-tea.jpg',
    imageSearchTerm: 'fennel herbal tea mug brewed warm drink',
    imageCredit: 'Unsplash',
    herbalTea: true
  },
  {
    id: 'khichdi-digestive',
    name: 'Digestive Mung Khichdi',
    category: RecipeCategory.DIGESTIVE,
    ailmentTags: ['bloating', 'diarrhea', 'gut reset', 'weak digestion'],
    doshaSuitability: ['All'],
    condition: 'acute',
    prepTime: '30 min',
    servings: 2,
    ingredients: [
      '½ cup split mung dal (yellow)',
      '¼ cup basmati rice',
      '1 tsp ghee',
      '1 pinch hing (asafoetida)',
      '½ tsp cumin seeds',
      '½ tsp turmeric',
      '1 inch ginger, grated',
      '3 cups water',
      'Rock salt to taste'
    ],
    steps: [
      'Rinse and soak dal and rice for 30 minutes.',
      'Heat ghee in a pot; add hing, cumin seeds, and ginger.',
      'Add drained dal and rice with turmeric and salt.',
      'Add 3-4 cups of water (more for a soupier consistency).',
      'Pressure cook or simmer for 25-30 minutes until very soft.',
      'Mash slightly before serving warm.'
    ],
    ayurvedicNote: 'This thin, soupy version (Peya) is specifically designed to reset the digestive system after infection or excess.',
    therapeuticBenefit: 'Extremely easy to absorb; stabilizes bowel movements and restores hydration.',
    imageUrl: '/images/nutriheal/khichdi-digestive.jpg',
    imageSearchTerm: 'mung dal khichdi yellow lentil rice bowl',
    imageCredit: 'Unsplash',
    herbalTea: false
  },
  {
    id: 'lentil-soup-simple',
    name: 'Simple Red Lentil Soup',
    category: RecipeCategory.DIGESTIVE,
    ailmentTags: ['fatigue', 'indigestion', 'liver detox'],
    doshaSuitability: ['All'],
    condition: 'both',
    prepTime: '25 min',
    servings: 2,
    ingredients: [
      '½ cup red lentils (masoor dal)',
      '1 medium tomato, chopped',
      '1 inch ginger, minced',
      '½ tsp turmeric',
      '1 tsp olive oil or ghee',
      '½ lemon, juiced',
      '2.5 cups water',
      'Salt and pepper to taste'
    ],
    steps: [
      'Rinse lentils thoroughly until water runs clear.',
      'Boil lentils with water, ginger, and turmeric for 15-20 minutes until creamy.',
      'Stir in chopped tomatoes and cook for another 5 minutes.',
      'Add salt, pepper, and olive oil.',
      'Finish with fresh lemon juice before serving warm.'
    ],
    ayurvedicNote: 'Red lentils are easier to source globally and provide a cooling, light protein source that doesn\'t aggravate Pitta.',
    therapeuticBenefit: 'High in plant-based protein and iron; supports liver detoxification and sustained energy.',
    imageUrl: '/images/nutriheal/lentil-soup-simple.jpg',
    imageSearchTerm: 'red lentil soup bowl turmeric warm',
    imageCredit: 'Unsplash',
    herbalTea: false
  },
  {
    id: 'fermented-rice-kanji',
    name: 'Fermented Rice Kanji',
    category: RecipeCategory.DIGESTIVE,
    ailmentTags: ['gut flora imbalance', 'acidity', 'fatigue', 'low immunity'],
    doshaSuitability: ['Pitta', 'Vata'],
    condition: 'chronic',
    prepTime: '5 min (+12h soak)',
    servings: 1,
    ingredients: [
      '½ cup cooked white rice',
      '1.5 cups water',
      'Pinch of rock salt',
      '5-6 curry leaves',
      'Optional: 1 small green chili, slit'
    ],
    steps: [
      'Place cooked rice in a clay pot or glass bowl.',
      'Add water until rice is submerged. Cover with a breathable cloth.',
      'Let it sit at room temperature for 12-24 hours (depending on climate).',
      'In the morning, mash the rice slightly in the water.',
      'Add salt, curry leaves, and green chili for flavor.',
      'Consume the liquid and rice as a probiotic breakfast.'
    ],
    ayurvedicNote: 'Known as "Pakhala", this ancient fermented food is a natural probiotic that cools the body and restores Agni.',
    therapeuticBenefit: 'Rich in Vitamin B12 and beneficial bacteria; excellent for restoring gut health after antibiotics.',
    imageUrl: '/images/nutriheal/fermented-rice-kanji.jpg',
    imageSearchTerm: 'fermented rice water kanji pakhala probiotic',
    imageCredit: 'NatureNani',
    herbalTea: false
  },
  // --- CARDIOVASCULAR ---
  {
    id: 'beetroot-carrot-juice',
    name: 'Beetroot & Carrot Detox Tonic',
    category: RecipeCategory.CARDIOVASCULAR,
    ailmentTags: ['high blood pressure', 'anemia', 'circulation'],
    doshaSuitability: ['Pitta', 'Kapha'],
    condition: 'chronic',
    prepTime: '10 min',
    servings: 1,
    ingredients: [
      '1 medium beetroot, peeled',
      '2 large carrots',
      '1 inch fresh ginger',
      '½ lemon, juiced',
      'Pinch of black pepper'
    ],
    steps: [
      'Wash and prep all vegetables.',
      'Pass beetroot, carrots, and ginger through a juicer.',
      'Collect the juice and stir in the lemon juice.',
      'Add a pinch of black pepper to enhance absorption.',
      'Drink immediately on an empty stomach or between meals.'
    ],
    ayurvedicNote: 'Beetroot is "Rakta Vardhaka" (blood-building) and helps purify the liver, while carrot provides a Pitta-neutral base.',
    therapeuticBenefit: 'Rich in nitrates that help dilate blood vessels, naturally lowering blood pressure and improving oxygen delivery.',
    imageUrl: '/images/nutriheal/beetroot-carrot-juice.jpg',
    imageSearchTerm: 'beetroot carrot juice glass red fresh',
    imageCredit: 'Unsplash',
    herbalTea: false
  },
  {
    id: 'hibiscus-rose-tea',
    name: 'Hibiscus Rose Petal Tea',
    category: RecipeCategory.HERBAL_TEAS,
    ailmentTags: ['high blood pressure', 'stress', 'hot flashes'],
    doshaSuitability: ['Pitta', 'Kapha'],
    condition: 'chronic',
    prepTime: '10 min',
    servings: 1,
    ingredients: [
      '1 tbsp dried hibiscus flowers',
      '1 tsp dried rose petals',
      '1 small cinnamon stick',
      '1.5 cups water',
      '½ tsp honey (optional)'
    ],
    steps: [
      'Bring water to a boil.',
      'Add hibiscus, rose petals, and cinnamon.',
      'Turn off heat and steep for 5-7 minutes.',
      'Strain into a cup.',
      'Add honey if needed once the tea has cooled slightly.',
      'Drink 1-2 times daily.'
    ],
    ayurvedicNote: 'Hibiscus (Japa) is renowned for its heart-cooling properties and its ability to manage "Rakta-Pitta" (bleeding or inflammatory blood disorders).',
    therapeuticBenefit: 'Clinical studies show hibiscus tea can be as effective as some blood pressure medications in moderate cases.',
    imageUrl: '/images/nutriheal/hibiscus-rose-tea.jpg',
    imageSearchTerm: 'hibiscus tea red pink cup warm flower',
    imageCredit: 'Unsplash',
    herbalTea: true
  },
  // --- RENAL ---
  {
    id: 'barley-water',
    name: 'Barley Water Kidney Flush',
    category: RecipeCategory.RENAL,
    ailmentTags: ['uti', 'kidney health', 'edema', 'water retention'],
    doshaSuitability: ['Kapha', 'Pitta'],
    condition: 'acute',
    prepTime: '45 min',
    servings: 2,
    ingredients: [
      '¼ cup pearl barley',
      '4 cups water',
      '½ lemon, juiced',
      'Optional: 1 tsp honey'
    ],
    steps: [
      'Rinse barley under cold water.',
      'Combine barley and water in a pot. Bring to a boil.',
      'Reduce heat and simmer for 30-40 minutes until the water is slightly cloudy and reduced by half.',
      'Strain the water into a pitcher (you can save the cooked barley for salad).',
      'Add lemon juice when cool.',
      'Sip throughout the day.'
    ],
    ayurvedicNote: 'Barley (Yava) is the premier grain for the urinary system — it is a gentle diuretic that clears toxins without depleting minerals.',
    therapeuticBenefit: 'Flushes the kidneys and urinary tract, reducing burning sensations and systemic swelling.',
    imageUrl: '/images/nutriheal/barley-water.jpg',
    imageSearchTerm: 'barley water glass clear lemon healthy drink',
    imageCredit: 'Unsplash',
    herbalTea: false
  },
  // --- NEUROLOGICAL ---
  {
    id: 'brahmi-ashwagandha-milk',
    name: 'Brahmi Ashwagandha Moon Milk',
    category: RecipeCategory.NEUROLOGICAL,
    ailmentTags: ['anxiety', 'insomnia', 'stress', 'brain fog'],
    doshaSuitability: ['Vata', 'Pitta'],
    condition: 'chronic',
    prepTime: '10 min',
    servings: 1,
    ingredients: [
      '1 cup warm milk (dairy or almond)',
      '½ tsp ashwagandha powder',
      '¼ tsp brahmi powder',
      'Pinch of nutmeg',
      '1 tsp ghee or honey'
    ],
    steps: [
      'Heat milk in a small saucepan until warm.',
      'Whisk in ashwagandha and brahmi powders.',
      'Add a pinch of nutmeg.',
      'Stir in ghee or honey.',
      'Drink 30 minutes before bedtime.'
    ],
    ayurvedicNote: 'Brahmi is a "Medhya Rasayana" (brain tonic) that balances the nervous system, while Ashwagandha provides deep grounding for Vata.',
    therapeuticBenefit: 'Promotes deep, restorative sleep and reduces cortisol levels to manage chronic anxiety.',
    imageUrl: '/images/nutriheal/brahmi-ashwagandha-milk.jpg',
    imageSearchTerm: 'warm ashwagandha milk mug bedtime drink',
    imageCredit: 'Unsplash',
    herbalTea: false
  },
  // --- IMMUNE ---
  {
    id: 'amla-ginger-shot',
    name: 'Amla Ginger Immunity Shot',
    category: RecipeCategory.IMMUNE,
    ailmentTags: ['low immunity', 'fatigue', 'recovery'],
    doshaSuitability: ['All'],
    condition: 'both',
    prepTime: '5 min',
    servings: 1,
    ingredients: [
      '1 tbsp amla juice (or 1 tsp amla powder)',
      '1 tsp ginger juice',
      'Pinch of black pepper',
      '1 tsp honey',
      '2 tbsp warm water'
    ],
    steps: [
      'Combine amla and ginger juice in a small glass.',
      'Add warm water and stir.',
      'Add black pepper and honey.',
      'Consume as a single shot on an empty stomach in the morning.'
    ],
    ayurvedicNote: 'Amla (Amalaki) is the richest natural source of Vitamin C and is one of the few substances that balances all three doshas.',
    therapeuticBenefit: 'Provides a concentrated dose of antioxidants to boost white blood cell production and speed up recovery.',
    imageUrl: '/images/nutriheal/amla-ginger-shot.jpg',
    imageSearchTerm: 'amla ginger immunity shot small glass',
    imageCredit: 'Unsplash',
    herbalTea: false
  },
  {
    id: 'rice-congee-recovery',
    name: 'Plain Rice Congee (Jook)',
    category: RecipeCategory.IMMUNE,
    ailmentTags: ['fever', 'recovery', 'stomach flu', 'weakness'],
    doshaSuitability: ['All'],
    condition: 'acute',
    prepTime: '60 min',
    servings: 2,
    ingredients: [
      '½ cup white rice (basmati or jasmine)',
      '4-5 cups water or weak vegetable broth',
      '1 inch ginger, smashed',
      'Pinch of salt',
      'Optional: a drop of sesame oil'
    ],
    steps: [
      'Rinse rice thoroughly.',
      'In a large pot, combine rice, water, and ginger.',
      'Bring to a boil, then reduce heat to very low.',
      'Cover and simmer for 45-60 minutes, stirring occasionally to prevent sticking.',
      'Cook until the rice has completely broken down into a creamy, thick soup.',
      'Season with a tiny pinch of salt and serve warm.'
    ],
    ayurvedicNote: 'This is the universal "Yavagu" or "Peja" mentioned in ancient texts as the most important food for the recovery phase of any fever (Jwara).',
    therapeuticBenefit: 'Minimal caloric effort for maximal hydration and easy nutrient absorption during acute illness.',
    imageUrl: '/images/nutriheal/rice-congee.jpg',
    imageSearchTerm: 'rice congee jook porridge bowl white warm',
    imageCredit: 'Unsplash',
    herbalTea: false
  },
  // --- CARDIOVASCULAR ---
  {
    id: 'oats-berry-bowl',
    name: 'Oat & Berry Healing Bowl',
    category: RecipeCategory.CARDIOVASCULAR,
    ailmentTags: ['high cholesterol', 'heart health', 'fatigue'],
    doshaSuitability: ['All'],
    condition: 'chronic',
    prepTime: '15 min',
    servings: 1,
    ingredients: [
      '½ cup rolled oats',
      '1 cup water or walnut milk',
      '¼ cup blueberries',
      '1 tbsp ground flaxseeds',
      '5 walnuts, chopped',
      '½ tsp cinnamon',
      '1 tsp honey'
    ],
    steps: [
      'Bring water or milk to a boil and add oats.',
      'Reduce heat and simmer for 10 minutes until soft.',
      'Stir in cinnamon and flaxseeds.',
      'Top with blueberries and walnuts.',
      'Drizzle with honey once slightly cooled.'
    ],
    ayurvedicNote: 'Oats are "Brimhana" (nourishing) and "Hridya" (heart-pleasing). Cinnamon improves circulation and manages blood sugar.',
    therapeuticBenefit: 'Provides omega-3 fatty acids and soluble fiber (beta-glucan) to reduce LDL cholesterol and support heart health.',
    imageUrl: '/images/nutriheal/oats-berry-bowl.jpg',
    imageSearchTerm: 'oatmeal blueberry walnut bowl breakfast healthy',
    imageCredit: 'Unsplash',
    herbalTea: false
  },
  {
    id: 'dal-spinach-soup',
    name: 'Dal & Spinach Heart Soup',
    category: RecipeCategory.CARDIOVASCULAR,
    ailmentTags: ['high blood pressure', 'circulation', 'inflammation'],
    doshaSuitability: ['Pitta', 'Kapha'],
    condition: 'both',
    prepTime: '25 min',
    servings: 2,
    ingredients: [
      '½ cup yellow toor dal or mung dal',
      '2 cups fresh spinach, chopped',
      '1 tomato, chopped',
      '1 tsp garlic, minced',
      '½ tsp turmeric',
      '½ tsp cumin seeds',
      '2.5 cups water',
      'Lemon juice'
    ],
    steps: [
      'Cook dal in water with turmeric and salt until soft.',
      'In a separate pan, sauté cumin and garlic in a little oil.',
      'Add tomatoes and cook until soft.',
      'Add the cooked dal and chopped spinach. Simmer for 5 minutes.',
      'Finish with a squeeze of fresh lemon juice before serving.'
    ],
    ayurvedicNote: 'Spinach is cooling and rich in iron, while garlic and cumin support the "Vyana Vayu" (the sub-dosha of Vata that governs circulation).',
    therapeuticBenefit: 'High in folate and potassium, helping to reduce homocysteine and regulate blood pressure.',
    imageUrl: '/images/nutriheal/dal-spinach-soup.jpg',
    imageSearchTerm: 'spinach lentil dal soup bowl healthy green',
    imageCredit: 'Unsplash',
    herbalTea: false
  },
  // --- RENAL ---
  {
    id: 'cucumber-mint-tonic',
    name: 'Cucumber Mint Cooling Tonic',
    category: RecipeCategory.HERBAL_TEAS,
    ailmentTags: ['uti', 'kidney heat', 'dehydration', 'burning sensation', 'pitta'],
    doshaSuitability: ['Pitta'],
    condition: 'acute',
    prepTime: '5 min',
    servings: 1,
    ingredients: [
      '1 cucumber, peeled and sliced',
      '10 fresh mint leaves',
      '½ lime, juiced',
      '1 cup coconut water',
      'Pinch of rock salt'
    ],
    steps: [
      'Blend cucumber, mint, and coconut water until smooth.',
      'Strain if a clear liquid is preferred.',
      'Add lime juice and a tiny pinch of rock salt.',
      'Drink chilled but not iced.'
    ],
    ayurvedicNote: 'Cucumber and coconut water are deeply "Sheeta" (cooling) and effectively clear heat from the urinary tract (Mutravaha Srotas).',
    therapeuticBenefit: 'Natural electrolyte-rich diuretic that reduces burning sensations and hydrates the kidneys.',
    imageUrl: '/images/nutriheal/cucumber-mint-tonic.jpg',
    imageSearchTerm: 'cucumber mint water glass fresh green tonic',
    imageCredit: 'Unsplash',
    herbalTea: true
  },
  {
    id: 'moong-soup-light',
    name: 'Light Whole Moong Soup',
    category: RecipeCategory.RENAL,
    ailmentTags: ['kidney health', 'proteinuria', 'detox'],
    doshaSuitability: ['All'],
    condition: 'chronic',
    prepTime: '40 min (+8h soaking)',
    servings: 2,
    ingredients: [
      '½ cup whole green mung beans',
      '3 cups water',
      '½ tsp ginger, grated',
      '½ tsp turmeric',
      '1 tsp ghee',
      'Pinch of black salt'
    ],
    steps: [
      'Soak whole mung beans overnight (8 hours).',
      'Boil in 3 cups of water with ginger and turmeric until beans are split and soft.',
      'Whisk slightly to thicken the liquid.',
      'Season with ghee and a tiny bit of black salt.',
      'Serve warm as a liquid soup.'
    ],
    ayurvedicNote: 'Whole mung beans are one of the few high-protein foods that do not put significant strain on the kidneys during digestion.',
    therapeuticBenefit: 'Provides low-impact protein and clears systemic inflammation, supporting the renal filtration process.',
    imageUrl: '/images/nutriheal/moong-soup-light.jpg',
    imageSearchTerm: 'whole mung bean soup bowl green light',
    imageCredit: 'Unsplash',
    herbalTea: false
  },
  // --- DERMATOLOGICAL ---
  {
    id: 'neem-turmeric-tea',
    name: 'Neem & Turmeric Detox Tea',
    category: RecipeCategory.HERBAL_TEAS,
    ailmentTags: ['acne', 'eczema', 'skin clearing', 'detox'],
    doshaSuitability: ['Pitta', 'Kapha'],
    condition: 'chronic',
    prepTime: '10 min',
    servings: 1,
    ingredients: [
      '1 tsp dried neem leaves (or 1 neem tea bag)',
      '½ tsp turmeric powder',
      '1 thin slice ginger',
      '1.5 cups water'
    ],
    steps: [
      'Boil water with ginger and turmeric.',
      'Add neem leaves and simmer on low for 5 minutes.',
      'Strain into a cup.',
      'Drink warm. Note: it will be very bitter, which is its medicinal nature.'
    ],
    ayurvedicNote: 'Neem is the most powerful "Tikta" (bitter) herb in Ayurveda for purifying the blood (Rakta Shodhana) and clearing skin heat.',
    therapeuticBenefit: 'Antifungal and antibacterial properties help clear acne and inflammatory skin conditions from the inside out.',
    imageUrl: '/images/nutriheal/neem-turmeric-tea.jpg',
    imageSearchTerm: 'turmeric herbal tea cup warm yellow drink',
    imageCredit: 'Unsplash',
    herbalTea: true
  },
  {
    id: 'avocado-greens-bowl',
    name: 'Avocado & Leafy Greens Bowl',
    category: RecipeCategory.DERMATOLOGICAL,
    ailmentTags: ['dry skin', 'eczema', 'anti-aging'],
    doshaSuitability: ['Vata', 'Pitta'],
    condition: 'chronic',
    prepTime: '15 min',
    servings: 1,
    ingredients: [
      '½ ripe avocado, sliced',
      '2 cups steamed kale or spinach',
      '2 tbsp pumpkin seeds',
      '1 tbsp extra virgin olive oil',
      'Lemon juice and rock salt'
    ],
    steps: [
      'Steam the greens until bright green and tender.',
      'Place in a bowl and top with avocado slices.',
      'Sprinkle pumpkin seeds over the top.',
      'Drizzle with olive oil and lemon juice.',
      'Season with a pinch of rock salt.'
    ],
    ayurvedicNote: 'Healthy fats (Sneha) are essential for pacifying the dryness of Vata that leads to skin cracking and aging.',
    therapeuticBenefit: 'Provides high doses of Vitamin E and healthy fats to restore the skin\'s natural moisture barrier.',
    imageUrl: '/images/nutriheal/avocado-greens-bowl.jpg',
    imageSearchTerm: 'avocado leafy greens bowl healthy meal',
    imageCredit: 'Unsplash',
    herbalTea: false
  },
  // --- NEUROLOGICAL ---
  {
    id: 'walnut-dark-choc-mix',
    name: 'Walnut & Dark Chocolate Brain Boost',
    category: RecipeCategory.NEUROLOGICAL,
    ailmentTags: ['focus', 'memory', 'brain fog', 'low energy'],
    doshaSuitability: ['Vata', 'Kapha'],
    condition: 'chronic',
    prepTime: '5 min',
    servings: 1,
    ingredients: [
      '10 raw walnut halves',
      '1 square dark chocolate (70%+ cocoa)',
      '1 tbsp pumpkin seeds',
      '1 tsp flaxseed'
    ],
    steps: [
      'In a small bowl, combine walnuts and pumpkin seeds.',
      'Chop the dark chocolate into small pieces and mix in.',
      'Sprinkle with flaxseeds.',
      'Consume as a focused mid-morning or afternoon snack.'
    ],
    ayurvedicNote: 'Walnuts resemble the brain (Akshota) and were traditionally used for brain health due to their signature-like quality.',
    therapeuticBenefit: 'Rich in omega-3 fatty acids and flavonoids that improve blood flow to the brain and enhance cognitive function.',
    imageUrl: '/images/nutriheal/walnut-dark-choc-mix.jpg',
    imageSearchTerm: 'walnuts dark chocolate snack bowl healthy',
    imageCredit: 'Unsplash',
    herbalTea: false
  },
  // --- MUSCULOSKELETAL ---
  {
    id: 'bone-broth-turmeric',
    name: 'Turmeric Healing Broth',
    category: RecipeCategory.MUSCULOSKELETAL,
    ailmentTags: ['joint pain', 'arthritis', 'bone health'],
    doshaSuitability: ['Vata', 'Kapha'],
    condition: 'chronic',
    prepTime: '60 min',
    servings: 4,
    ingredients: [
      '1 kg marrow bones or 500g mushrooms (for veg)',
      '1 tbsp apple cider vinegar',
      '1 tsp turmeric powder',
      '1 inch ginger, smashed',
      '1 tbsp black peppercorns',
      '2 liters water'
    ],
    steps: [
      'Combine bones/mushrooms, vinegar, and water in a large pot.',
      'Bring to a boil, then reduce to a very low simmer.',
      'Add turmeric, ginger, and peppercorns.',
      'Simmer for at least 1 hour (longer for bones).',
      'Strain and drink warm or use as a base for other soups.'
    ],
    ayurvedicNote: 'Bone marrow (Majja) and high-mineral mushrooms help replenish the tissues (Dhatus) responsible for skeletal integrity.',
    therapeuticBenefit: 'Provides collagen and essential minerals to repair joint cartilage and strengthen bones.',
    imageUrl: '/images/nutriheal/bone-broth-turmeric.jpg',
    imageSearchTerm: 'turmeric bone broth warm mug golden steam',
    imageCredit: 'Unsplash',
    herbalTea: false
  },
  {
    id: 'sesame-flaxseed-ladoo',
    name: 'Sesame Flaxseed Bone Bites',
    category: RecipeCategory.MUSCULOSKELETAL,
    ailmentTags: ['osteoporosis', 'joint weakness', 'stiffness'],
    doshaSuitability: ['Vata'],
    condition: 'chronic',
    prepTime: '20 min',
    servings: 10,
    ingredients: [
      '1 cup black or white sesame seeds',
      '¼ cup flaxseeds',
      '½ cup jaggery or dates',
      '1 tsp ghee',
      '½ tsp cardamom'
    ],
    steps: [
      'Dry roast sesame and flaxseeds until they pop.',
      'Cool and pulse in a blender until coarse.',
      'Melt jaggery with ghee in a pan until syrupy.',
      'Mix in the seed powder and cardamom.',
      'While still warm, roll into small bite-sized balls.'
    ],
    ayurvedicNote: 'Sesame (Tila) is the highest Ayurvedic source of calcium and is specifically used to nourish the "Asthi Dhatu" (bone tissue).',
    therapeuticBenefit: 'Natural, energy-dense calcium and omega-3 supplement for skeletal health and joint lubrication.',
    imageUrl: '/images/nutriheal/sesame-flaxseed-ladoo.jpg',
    imageSearchTerm: 'sesame energy ball snack round plate',
    imageCredit: 'Unsplash',
    herbalTea: false
  },
  {
    id: 'sprouted-mung-hemp-bowl',
    name: 'Sprouted Mung & Hemp Power Bowl',
    category: RecipeCategory.MUSCULOSKELETAL,
    ailmentTags: ['muscle recovery', 'post-workout', 'workout', 'muscle health', 'protein', 'recovery', 'strength'],
    doshaSuitability: ['Pitta', 'Kapha', 'Vata'],
    condition: 'both',
    prepTime: '15 min',
    servings: 2,
    ingredients: [
      '1.5 cups sprouted mung beans (steamed 5 mins)',
      '2 tbsp raw shelled hemp seeds',
      '2 tbsp raw pumpkin seeds (pepitas)',
      '½ diced cucumber or steamed zucchini',
      '1 tbsp cold-pressed sesame oil or ghee',
      '½ tsp cumin powder & pinch of rock salt',
      '1 tbsp fresh lime juice'
    ],
    steps: [
      'Lightly steam the sprouted mung beans for 5 minutes so they remain tender and easily digestible.',
      'Transfer to a bowl and toss with cucumber/zucchini.',
      'Drizzle with sesame oil or warm ghee, lime juice, cumin, and rock salt.',
      'Top generously with hemp seeds and raw pumpkin seeds for a dense amino acid and zinc boost.',
      'Serve warm or at room temperature within 1 hour post-workout.'
    ],
    ayurvedicNote: 'Sprouted mung is light (Laghu) yet rich in bioavailable plant protein (Mamsa Dhatu builder), while hemp and pumpkin seeds provide essential healthy fats without aggravating Pitta heat.',
    therapeuticBenefit: 'Delivers 18g+ of clean, complete plant protein and magnesium to repair micro-tears in muscle fibers and restore depleted energy after intense training.',
    imageUrl: '/images/nutriheal/sprouted-mung-hemp-bowl.jpg',
    imageSearchTerm: 'sprouted mung bean salad bowl seeds avocado healthy',
    imageCredit: 'Unsplash',
    herbalTea: false
  },
  {
    id: 'almond-ashwagandha-elixir',
    name: 'Almond Ashwagandha Muscle Recovery Elixir',
    category: RecipeCategory.MUSCULOSKELETAL,
    ailmentTags: ['muscle recovery', 'post-workout', 'workout', 'fatigue', 'muscle soreness', 'strength'],
    doshaSuitability: ['Pitta', 'Vata'],
    condition: 'both',
    prepTime: '10 min',
    servings: 1,
    ingredients: [
      '1 cup warm almond milk or coconut milk',
      '½ tsp Ashwagandha root powder',
      '1 tbsp chia seeds or soaked almonds (blended)',
      '2 pitted Medjool dates (for natural glucose replenishment)',
      '¼ tsp green cardamom powder',
      '½ tsp pure ghee'
    ],
    steps: [
      'Warm almond milk in a small saucepan over medium heat.',
      'Add Ashwagandha, cardamom, dates, and ghee to a blender.',
      'Pour in the warm milk and blend on high for 30 seconds until frothy and smooth.',
      'Stir in chia seeds and let sit for 2 minutes before sipping slowly.'
    ],
    ayurvedicNote: 'Ashwagandha is a premier Rasayana (rejuvenator) that rebuilds muscle strength (Balya) and lowers post-exercise cortisol levels, combined with cooling almond fat.',
    therapeuticBenefit: 'Replenishes muscle glycogen with natural date sugars while healthy fats and adaptogens accelerate neuromuscular recovery.',
    imageUrl: '/images/nutriheal/almond-ashwagandha-elixir.jpg',
    imageSearchTerm: 'golden warm almond milk elixir cinnamon mug',
    imageCredit: 'Unsplash',
    herbalTea: false
  },
  {
    id: 'golden-lentil-seed-stew',
    name: 'Golden Spiced Lentil & Seed Stew',
    category: RecipeCategory.MUSCULOSKELETAL,
    ailmentTags: ['muscle recovery', 'post-workout', 'muscle health', 'joint pain', 'stiffness'],
    doshaSuitability: ['Pitta', 'Kapha', 'Vata'],
    condition: 'both',
    prepTime: '30 min',
    servings: 3,
    ingredients: [
      '1 cup red lentils (masoor dal), rinsed',
      '2 tbsp coconut cream or ghee',
      '1 tbsp toasted white sesame seeds',
      '1 tsp turmeric powder',
      '1 inch fresh ginger, finely grated',
      '3 cups vegetable broth or water',
      '1 cup baby spinach',
      'Pinch of black pepper and sea salt'
    ],
    steps: [
      'In a medium pot, heat ghee or coconut cream. Add ginger and turmeric and sauté for 30 seconds.',
      'Add red lentils and stir to coat in spices.',
      'Pour in vegetable broth, bring to a boil, then reduce heat and simmer for 20 minutes until lentils are creamy.',
      'Fold in baby spinach until wilted.',
      'Ladle into bowls and sprinkle with toasted sesame seeds for healthy calcium and essential fatty acids.'
    ],
    ayurvedicNote: 'Red lentils provide rapid plant protein absorption; pairing them with ginger, turmeric, and healthy fats balances all three doshas while reducing exercise-induced tissue inflammation.',
    therapeuticBenefit: 'Provides sustained protein, iron, and anti-inflammatory compounds for deep muscle repair and joint protection.',
    imageUrl: '/images/nutriheal/golden-lentil-seed-stew.jpg',
    imageSearchTerm: 'golden red lentil soup bowl spinach seeds',
    imageCredit: 'Unsplash',
    herbalTea: false
  },
  // --- ENDOCRINE ---
  {
    id: 'bitter-melon-juice',
    name: 'Bitter Melon & Turmeric Juice',
    category: RecipeCategory.ENDOCRINE,
    ailmentTags: ['diabetes', 'high blood sugar', 'metabolism'],
    doshaSuitability: ['Kapha', 'Pitta'],
    condition: 'chronic',
    prepTime: '10 min',
    servings: 1,
    ingredients: [
      '1 medium bitter melon (karela)',
      '½ inch ginger',
      '½ tsp turmeric',
      '½ lemon, juiced',
      '½ cup water'
    ],
    steps: [
      'Remove seeds from bitter melon and chop.',
      'Blend with ginger and water.',
      'Strain the juice into a glass.',
      'Add turmeric and lemon juice.',
      'Drink on an empty stomach in the morning.'
    ],
    ayurvedicNote: 'Bitter melon (Kavellaka) is the supreme "Prameha-hara" (anti-diabetic) food in Ayurveda due to its intense bitter quality.',
    therapeuticBenefit: 'Contains polypeptide-p, a plant insulin that helps regulate blood glucose levels naturally.',
    imageUrl: '/images/nutriheal/bitter-melon-juice.jpg',
    imageSearchTerm: 'bitter melon green juice glass health drink',
    imageCredit: 'Unsplash',
    herbalTea: false
  },
  {
    id: 'methi-seed-tea',
    name: 'Fenugreek Seed Soaked Water',
    category: RecipeCategory.HERBAL_TEAS,
    ailmentTags: ['diabetes', 'hormone balance', 'pcos'],
    doshaSuitability: ['Kapha', 'Vata'],
    condition: 'chronic',
    prepTime: '5 min (+8h soak)',
    servings: 1,
    ingredients: [
      '1 tsp fenugreek (methi) seeds',
      '1 cup water'
    ],
    steps: [
      'Soak seeds in water overnight.',
      'In the morning, drink the water on an empty stomach.',
      'Chew and swallow the softened seeds for maximum effect.'
    ],
    ayurvedicNote: 'Fenugreek (Methi) seeds are warming and stimulate the pancreatic fire (Amashaya Agni) to improve glucose metabolism.',
    therapeuticBenefit: 'Improves insulin sensitivity and helps regulate the menstrual cycle in hormonal conditions like PCOS.',
    imageUrl: '/images/nutriheal/methi-seed-tea.jpg',
    imageSearchTerm: 'fenugreek water glass soaked seeds morning',
    imageCredit: 'Unsplash',
    herbalTea: true
  },
  // --- IMMUNE ---
  {
    id: 'turmeric-muscle-latte',
    name: 'Turmeric Muscle Recovery Latte',
    category: RecipeCategory.HERBAL_TEAS,
    ailmentTags: ['muscle recovery', 'post-workout', 'workout', 'strength', 'fatigue', 'muscle soreness'],
    doshaSuitability: ['All'],
    condition: 'both',
    prepTime: '10 min',
    servings: 1,
    ingredients: [
      '1 cup warm oat milk or almond milk',
      '½ tsp turmeric powder',
      '¼ tsp black pepper (enhances curcumin absorption 2000%)',
      '¼ tsp cinnamon',
      '1 tsp raw honey',
      '½ tsp ghee or coconut oil'
    ],
    steps: [
      'Warm the milk in a small saucepan over medium-low heat.',
      'Add turmeric, black pepper, and cinnamon. Whisk together until fully incorporated.',
      'Remove from heat and stir in ghee or coconut oil.',
      'Pour into a mug and add honey once slightly cooled.',
      'Sip within 30 minutes post-workout for maximum anti-inflammatory effect.'
    ],
    ayurvedicNote: 'Turmeric (Haridra) is the primary anti-inflammatory herb in Ayurveda, targeting the Rakta (blood) and Mamsa (muscle) dhatus directly. Black pepper\'s piperine makes curcumin 20x more bioavailable.',
    therapeuticBenefit: 'Reduces post-exercise muscle inflammation and DOMS (delayed onset muscle soreness) through potent curcumin activation.',
    imageUrl: '/images/nutriheal/turmeric-golden-milk.jpg',
    imageSearchTerm: 'turmeric golden milk latte mug warm',
    imageCredit: 'Unsplash',
    herbalTea: true
  }
];

// Helper to filter recipes
export const getRecipesByAilment = (ailmentSlug: string) => {
  const slug = ailmentSlug.toLowerCase();
  return NUTRIHEAL_RECIPES.filter(r =>
    r.ailmentTags.some(tag => slug.includes(tag) || tag.includes(slug))
  );
};

export const getRecipesByDosha = (dosha: DoshaType | 'sama') => {
  const d = dosha.toLowerCase() === 'all' || dosha.toLowerCase() === 'sama' ? 'All' : dosha as DoshaType;
  return NUTRIHEAL_RECIPES.filter(r => r.doshaSuitability.includes('All') || r.doshaSuitability.includes(d));
};

export const getHerbalTeasForAilment = (ailmentSlug: string) => {
  const slug = ailmentSlug.toLowerCase();
  return NUTRIHEAL_RECIPES.filter(r =>
    r.herbalTea && r.ailmentTags.some(tag => slug.includes(tag) || tag.includes(slug))
  );
};

export const STATIC_BASE_GUIDELINES: Record<string, AilmentGuidelines> = {
  RESPIRATORY: {
    embrace: [
      'Drink warm ginger-honey-lemon tea every 3-4 hours',
      'Eat only warm, freshly cooked meals to protect Agni',
      'Make Khichdi your anchor meal during illness',
      'Use turmeric generously in all cooking',
      'Include garlic and ginger to clear congestion'
    ],
    avoid: [
      'Cold water, ice, and refrigerated drinks',
      'Dairy milk and yogurt (increases mucous)',
      'Raw salads and uncooked vegetables',
      'Refined sugar and heavy processed foods',
      'Fried or oily preparations'
    ],
    switches: [
      'Wheat / White Rice \u2192 Millets or Brown Organic Rice',
      'Regular Cow\'s Milk \u2192 Coconut or Sesame Milk',
      'Raw Salad \u2192 Steamed Greens with Lemon',
      'Coffee \u2192 Ginger Tea or CCF Tea',
      'Refined Sugar \u2192 Raw Honey or Jaggery',
      'Cold Cereal \u2192 Warm Millet Porridge'
    ],
    timing: 'Eat light, warm meals 3 times a day. Avoid eating after sunset if congestion is heavy.'
  },
  DIGESTIVE: {
    embrace: [
      'Switch from wheat to millets (soaked 6h)',
      'Use mung dal as your primary protein source',
      'Include fermented foods like rice kanji or coconut kefir',
      'Cook all vegetables; avoid raw roughage',
      'Use digestive spices: cumin, coriander, fennel, hing'
    ],
    avoid: [
      'Wheat and refined flour (maida)',
      'Refined sugar and high-glycemic carbs',
      'Large legumes like chickpeas or kidney beans',
      'Ice cold drinks and frozen foods',
      'Eating while stressed or distracted'
    ],
    switches: [
      'Wheat / White Rice \u2192 Millets or Brown Organic Rice',
      'Regular Cow\'s Milk \u2192 Coconut or Sesame Milk',
      'Raw Salad \u2192 Steamed Greens with Lemon',
      'Coffee \u2192 Ginger Tea or CCF Tea',
      'Refined Sugar \u2192 Raw Honey or Jaggery',
      'Cold Cereal \u2192 Warm Millet Porridge'
    ],
    timing: 'Eat your largest meal at midday (12pm-1pm). Sip warm fennel tea after every meal.'
  },
  CARDIOVASCULAR: {
    embrace: [
      'Include nitrate-rich beets and carrots daily',
      'Use healthy omega-3 sources like walnuts and flax',
      'Drink hibiscus tea to manage blood pressure',
      'Favor light legumes (mung, red lentil) over heavy ones',
      'Use garlic and ginger to improve circulation'
    ],
    avoid: [
      'High-sodium processed salts and pickles',
      'Saturated fats and fried snacks',
      'Excessive caffeine and stimulants',
      'Heavy red meats and processed meats',
      'White refined sugar'
    ],
    switches: [
      'Wheat / White Rice \u2192 Millets or Brown Organic Rice',
      'Regular Cow\'s Milk \u2192 Coconut or Sesame Milk',
      'Raw Salad \u2192 Steamed Greens with Lemon',
      'Coffee \u2192 Ginger Tea or CCF Tea',
      'Refined Sugar \u2192 Raw Honey or Jaggery',
      'Cold Cereal \u2192 Warm Millet Porridge'
    ],
    timing: 'Eat moderate meals. Evening meal should be very light and at least 3 hours before bed.'
  },
  RENAL: {
    embrace: [
      'Drink barley water to flush the kidneys',
      'Use cooling cucumber and coconut water',
      'Switch to light, easily digestible mung soup',
      'Include hydrating, non-acidic fruits like melons',
      'Maintain steady, moderate hydration'
    ],
    avoid: [
      'High-potassium/sodium processed snacks',
      'Heavy animal protein (strains filtration)',
      'Spicy, pungent peppers and sauces',
      'Excessive caffeine and alcohol',
      'Nightshades if inflammation is high'
    ],
    switches: [
      'Wheat / White Rice \u2192 Millets or Brown Organic Rice',
      'Regular Cow\'s Milk \u2192 Coconut or Sesame Milk',
      'Raw Salad \u2192 Steamed Greens with Lemon',
      'Coffee \u2192 Ginger Tea or CCF Tea',
      'Refined Sugar \u2192 Raw Honey or Jaggery',
      'Cold Cereal \u2192 Warm Millet Porridge'
    ],
    timing: 'Hydrate consistently throughout the daylight hours. Reduce fluid intake 2 hours before bed.'
  },
  DERMATOLOGICAL: {
    embrace: [
      'Drink blood-purifying neem and turmeric infusions',
      'Include healthy fats (avocado, ghee) for skin barrier',
      'Focus on internal hydration with coconut water',
      'Use "bitter" greens to clear skin heat',
      'Favor cooling, Pitta-pacifying foods'
    ],
    avoid: [
      'Spicy, fermented, and sour foods (Pitta-aggravating)',
      'Refined sugar (fuels inflammation/acne)',
      'Fried and "heavy" oily snacks',
      'Excessive salt and alcohol',
      'Processed dairy (can trigger flares)'
    ],
    switches: [
      'Wheat / White Rice \u2192 Millets or Brown Organic Rice',
      'Regular Cow\'s Milk \u2192 Coconut or Sesame Milk',
      'Raw Salad \u2192 Steamed Greens with Lemon',
      'Coffee \u2192 Ginger Tea or CCF Tea',
      'Refined Sugar \u2192 Raw Honey or Jaggery',
      'Cold Cereal \u2192 Warm Millet Porridge'
    ],
    timing: 'Avoid eating late at night to ensure the liver can detoxify the blood during early morning hours.'
  },
  NEUROLOGICAL: {
    embrace: [
      'Drink warm Ashwagandha moon milk before bed',
      'Include soaked walnuts and seeds for brain fats',
      'Add Ghee to meals to nourish the nervous system',
      'Favor warm, grounding, and "mushy" comfort foods',
      'Use calming spices like nutmeg and cardamom'
    ],
    avoid: [
      'Cold, dry, and crunchy snack foods (Vata-aggravating)',
      'Excessive caffeine and energy drinks',
      'Irregular meal timings',
      'Light/skipped meals when stressed',
      'Raw roughage during high anxiety phases'
    ],
    switches: [
      'Wheat / White Rice \u2192 Millets or Brown Organic Rice',
      'Regular Cow\'s Milk \u2192 Coconut or Sesame Milk',
      'Raw Salad \u2192 Steamed Greens with Lemon',
      'Coffee \u2192 Ginger Tea or CCF Tea',
      'Refined Sugar \u2192 Raw Honey or Jaggery',
      'Cold Cereal \u2192 Warm Millet Porridge'
    ],
    timing: 'Routine is medicine. Eat at the exact same time daily to ground Vata energy.'
  },
  MUSCULOSKELETAL: {
    embrace: [
      'Prioritize bioavailable plant proteins (sprouted mung, red lentils, hemp & pumpkin seeds)',
      'Incorporate healthy fats (ghee, almond butter, sesame, coconut) for Mamsa Dhatu muscle repair',
      'Incorporate collagen-rich broths (bone or mushroom)',
      'Cook with anti-inflammatory turmeric, ginger, and Ashwagandha',
      'Include magnesium-rich leafy greens and seeds for muscle relaxation'
    ],
    avoid: [
      'Inflammatory refined sugars and processed grains',
      'Cold/iced drinks (stiffens joints and impairs nutrient absorption)',
      'Highly processed seed oils',
      'Heavy, hard-to-digest fried foods post-workout',
      'Excessive raw foods during acute muscle soreness'
    ],
    switches: [
      'Low-protein processed snacks → Sprouted Mung & Hemp Seed Bowls',
      'Sugary commercial protein shakes → Almond Ashwagandha Recovery Elixir',
      'Refined seed oils → Pure Ghee or Cold-pressed Sesame / Coconut Oil',
      'Cold iced drinks → Warm Herbal Electrolyte & Spice Infusions',
      'Refined Grains → Protein-rich Millets or Red Lentil Stew',
      'Regular Cow\'s Milk → Almond, Coconut, or Sesame Milk'
    ],
    timing: 'Consume a protein and healthy-fat rich meal within 45-60 minutes post-workout to maximize muscle glycogen & tissue repair.'
  },
  ENDOCRINE: {
    embrace: [
      'Include bitter-tasting foods to manage blood sugar',
      'Use soaked fenugreek seeds for hormone balance',
      'Focus on high-fiber grains like millets',
      'Eat low-glycemic, whole foods',
      'Support the liver with dandelion or neem'
    ],
    avoid: [
      'Refined white sugar and high-fructose syrups',
      'White flour and refined grain products',
      'Large, frequent meals (stick to 2-3 clean meals)',
      'Late night snacking',
      'Processed dairy and soy'
    ],
    switches: [
      'Wheat / White Rice \u2192 Millets or Brown Organic Rice',
      'Regular Cow\'s Milk \u2192 Coconut or Sesame Milk',
      'Raw Salad \u2192 Steamed Greens with Lemon',
      'Coffee \u2192 Ginger Tea or CCF Tea',
      'Refined Sugar \u2192 Raw Honey or Jaggery',
      'Cold Cereal \u2192 Warm Millet Porridge'
    ],
    timing: 'Follow a 12-14 hour overnight fast to allow insulin levels to stabilize.'
  },
  IMMUNE: {
    embrace: [
      'Take high-Vitamin C amla shots in the morning',
      'Use Tulsi to support the body\'s natural defenses',
      'Prioritize easy-to-digest congee during recovery',
      'Include antimicrobial garlic and turmeric',
      'Focus on warm, liquid-heavy nourishment'
    ],
    avoid: [
      'Heavy, oily, and difficult-to-digest foods',
      'Cold/refrigerated dairy and sweets',
      'Processed snacks that deplete energy',
      'Alcohol and excessive caffeine',
      'Raw food when energy is very low'
    ],
    switches: [
      'Wheat / White Rice \u2192 Millets or Brown Organic Rice',
      'Regular Cow\'s Milk \u2192 Coconut or Sesame Milk',
      'Raw Salad \u2192 Steamed Greens with Lemon',
      'Coffee \u2192 Ginger Tea or CCF Tea',
      'Refined Sugar \u2192 Raw Honey or Jaggery',
      'Cold Cereal \u2192 Warm Millet Porridge'
    ],
    timing: 'Listen to your hunger — if the appetite is low during illness, favor liquids over solid food.'
  }
};

export default NUTRIHEAL_RECIPES;
